import { Box, IconButton, SnackbarOrigin, SnackbarProps, SxProps, Typography, useMediaQuery, useTheme } from "@mui/material";

import { CloseSmallIcon, InfoFilledIcon, RadioOnIcon, WarningFilledIcon, WarningHardFilledIcon } from "src/foundations/icons";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { SenecButton } from "src/components/button/senec-button/SenecButton";
import { FlexBox, StyledSnackbar, TitleBox } from "src/components/feedback/senec-snackbar/SenecSnackbar.styled";
import Paper from "@mui/material/Paper";
import { useScreenSize } from "src/hooks/useScreenSize";

export type SnackbarButtonProps = {
    title: string;
    position?: "header" | "bottom";
    onClick?: () => void;
    "data-testid"?: string;
};

export type SenecSnackbarOrigin = {
    vertical: "top" | "center" | "bottom";
    horizontal: "left" | "center" | "right";
};
export interface SenecSnackbarProps extends Pick<SnackbarProps, "onClose" | "open" | "autoHideDuration" | "sx"> {
    /**
     * onClose - optional close handler
     */
    onClose?: (event: Event | React.SyntheticEvent<Element, Event>) => void;

    /**
     * severity - determines color and icon of snackbar
     */
    severity?: "error" | "warning" | "info" | "success";

    /**
     * title - title of the snackbar positioned on the top
     */
    title: string;

    /**
     * subtitle - optional subtitle positioned below the title
     */
    subtitle?: string;

    /**
     * button - action button with individually defined onClick
     */
    button?: SnackbarButtonProps;

    /**
     * anchorOrigin - if set snackbar is fixed, if not snackbar is relative positioned
     */
    anchorOrigin?: SenecSnackbarOrigin;
}

const iconStyles = { alignSelf: "start", marginRight: 1.5 };

const iconMapping = {
    warning: <WarningFilledIcon data-testid="warning-icon" sx={iconStyles} />,
    error: <WarningHardFilledIcon htmlColor="info" data-testid="error-icon" sx={iconStyles} />,
    info: <InfoFilledIcon data-testid="info-icon" sx={iconStyles} />,
    success: <RadioOnIcon data-testid="success-icon" sx={iconStyles} />,
};

export const SenecSnackbar = ({
    title,
    subtitle,
    open = true,
    onClose,
    button,
    severity = "info",
    autoHideDuration,
    anchorOrigin,
    sx,
}: SenecSnackbarProps) => {
    const { isMobile } = useScreenSize();
    const withBottomButton = button && (button.position === "bottom" || button.position === undefined || isMobile);

    return (
        <StyledSnackbar
            data-testid="snackbar"
            open={open}
            severity={severity}
            onClose={onClose}
            autoHideDuration={autoHideDuration}
            anchorOrigin={anchorOrigin as (SnackbarOrigin & SenecSnackbarOrigin) | undefined}
            sx={sx}
        >
            <Paper elevation={0}>
                <SnackbarHeader title={title} severity={severity} button={button} onClose={onClose} />
                {subtitle && (
                    <Box sx={{ paddingTop: "12px" }}>
                        <Typography variant={"s1"} sx={{ whiteSpace: "pre-line" }}>
                            {subtitle}
                        </Typography>
                    </Box>
                )}
                {withBottomButton && (
                    <SnackbarButton
                        sx={{
                            marginTop: "24px",
                            alignSelf: "end",
                        }}
                        buttonConfig={button}
                    />
                )}
            </Paper>
        </StyledSnackbar>
    );
};

const SnackbarHeader = ({
    severity,
    title,
    button,
    onClose,
}: Pick<SenecSnackbarProps, "severity" | "title" | "onClose" | "button">) => {
    const Icon = iconMapping[severity!];
    const theme = useTheme() as SenecBaseTheme;
    const { isMobile } = useScreenSize();
    const withHeaderButton = button && button.position === "header" && !isMobile;

    return (
        <TitleBox>
            <FlexBox>
                <FlexBox id="alertIcon">{Icon}</FlexBox>
                <Typography variant={"h5"}>{title}</Typography>
            </FlexBox>
            <Box sx={{ display: "flex", gap: "1rem", alignSelf: withHeaderButton ? "start" : "center" }}>
                {withHeaderButton && <SnackbarButton buttonConfig={button} />}
                {onClose && (
                    <IconButton
                        size="small"
                        aria-label="close"
                        onClick={onClose}
                        sx={{ marginLeft: "0 !important", alignSelf: withHeaderButton ? "center" : "start" }}
                    >
                        <CloseSmallIcon htmlColor={theme.palette.neutralGray100.main} />
                    </IconButton>
                )}
            </Box>
        </TitleBox>
    );
};

const SnackbarButton = ({ buttonConfig, sx }: { buttonConfig: SnackbarButtonProps; sx?: SxProps }) => {
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down("tablet"));

    return (
        <SenecButton
            sx={sx}
            fullWidth={isTablet}
            size={isTablet ? "medium" : "small"}
            level="secondary"
            onClick={buttonConfig.onClick}
            data-testid={buttonConfig["data-testid"]}
        >
            <Typography variant={isTablet ? "button" : "buttonSmall"}>{buttonConfig.title}</Typography>
        </SenecButton>
    );
};
